// @flow
import type {Node} from 'react'
import React, {Fragment, Suspense, useState, useCallback} from 'react'
import {graphql, useFragment, useLazyLoadQuery} from 'react-relay'
import {useOrganizationContext} from 'contexts/OrganizationContext'

import liftNodes from 'utils/GraphQL/typedLiftNodes'
import AddProjectMutation from 'mutations/AddProjectMutation'
import Projects from 'components/ui/icons/Projects'
import Actions from 'components/ui/icons/Actions'
import AgendaItems from 'components/ui/icons/AgendaItems'
import Proposals from 'components/ui/icons/Proposals'
import Checklists from 'components/ui/icons/Checklists'
import Metrics from 'components/ui/icons/Metrics'
import Goals from 'components/ui/icons/Goals'
import Inbox from 'components/ui/icons/Inbox'
import Add from 'components/ui/icons/md/Add'
import IconContainer from 'components/SideBar/ui/IconContainer'
import DisabledButtonWithTooltip from 'components/ui/DisabledButtonWithTooltip'
import ModalLoader from 'components/ui/ModalLoader'
import type {RenderActionButtonProps} from 'components/SideBar/ui/SectionItem'
import SectionItem from 'components/SideBar/ui/SectionItem'
import preloadedLazy from 'utils/MiscUtils/preloadedLazy'
import {type CalloutType} from 'components/ui/PremiumCallout'
import InboxNotifications from './InboxNotifications'
import ProposalsNotifications from './ProposalsNotifications'
import QuickAddButton from './QuickAddButton'
import styles from './index.scss'

import type {
  TensionProcessing_organization$key as Organization,
} from './__generated__/TensionProcessing_organization.graphql'
import type {
  TensionProcessingQuery as Query,
} from './__generated__/TensionProcessingQuery.graphql'
/* eslint-disable comma-dangle */

const LazyCreateAgendaItemModal = preloadedLazy(() => import(
  /* webpackPrefetch: true */
  /* webpackChunkName: "CreateAgendaItemModal" */
  'components/agendaItems/CreateAgendaItemModal'
))
const LazyProposalCreator = preloadedLazy(() => import(
  /* webpackPrefetch: true */
  /* webpackChunkName: "ProposalCreator" */
  'components/pages/Proposals/ProposalsView/ProposalCreator'
))
const LazyCreateProjectModal = preloadedLazy(() => import(
  /* webpackPrefetch: true */
  /* webpackChunkName: "CreateProjectModal" */
  'components/projects/CreateProjectModal'
))
const LazyChecklistCreator = preloadedLazy(() => import(
  /* webpackPrefetch: true */
  /* webpackChunkName: "CreateViewerChecklistItem" */
  'components/pages/Checklists/ChecklistList/CreateViewerChecklistItem'
))
const LazyMetricCreator = preloadedLazy(() => import(
  /* webpackPrefetch: true */
  /* webpackChunkName: "CreateViewerMetric" */
  'components/pages/Metrics/MetricList/CreateViewerMetric'
))

/* eslint-enable comma-dangle */

type Props = $ReadOnly<{
  organization: Organization,
}>

type OpenModal = 'Tension' | 'AgendaItem' | 'Project' | 'Proposal' | 'Checklist' | 'Metric'

type SectionItemProps = {
  key: string,
  title: string,
  renderIcon: () => Node,
  href: string,
  dataTurboFrame?: string | null,
  renderActionButton: ?(RenderActionButtonProps) => Node,
  renderBadge?: Node,
  premiumCallout?: ?CalloutType,
  premiumCalloutLinkable?: boolean,
}

const modalsQuery = graphql`
  query TensionProcessingQuery(
    $orgDatabaseId: String!,
  ) {
    organization(databaseId: $orgDatabaseId) {
      ...CreateAgendaItemModal_organization
      ...CreateProjectModal_organization
    }
  }
`

const modalsFragment = graphql`
  fragment TensionProcessing_organization on Organization {
    id
    databaseId
    governanceEnabled
    isAgendaItemsEnabled
    isApmAlphaEnabled
    isGoalsEnabled
    isHotwiredAlphaEnabled
    viewerCanAddProposal
    onRestrictedPlan

    viewer {
      id

      roles {
        edges {
          node {
            __typename
          }
        }
      }

      routes {
        actionsPath
        agendaItemsPath
        projectsPath
        proposalsPath
        checklistsPath
        metricsPath
        goalsPath
        inboxPath
        addTensionPath
        addActionPath
        workspacePath
      }

      ...ProposalsNotifications_viewer
      ...InboxNotifications_viewer
    }
  }
`

function TensionProcessing({organization: organizationKey}: Props): Node {
  const [addingModal, setAddingModal] = useState<?OpenModal>(null)
  const [tensionDatabaseId, setTensionDatabaseId] = useState<?string>(null)

  const organization = useFragment(modalsFragment, organizationKey)
  const organizationContext = useOrganizationContext()
  const queryResult = useLazyLoadQuery<Query>(modalsQuery, {orgDatabaseId: organization.databaseId})

  const routes = organization.viewer.routes

  const viewerCanAddProposal = organization.viewerCanAddProposal

  const showPremiumCallouts = organization.onRestrictedPlan

  const projectInitialValues = {
    personId: organization.viewer.id,
  }

  const openAddingModal = (name: OpenModal) => () => {
    setAddingModal(name)
  }

  const openProposalBuilder = (
    proposalTensionDatabaseId: ?string,
  ) => () => {
    if (!viewerCanAddProposal)
      return

    setAddingModal('Proposal')
    setTensionDatabaseId(proposalTensionDatabaseId)
  }

  const closeAddingModal = () => {
    setAddingModal(null)
  }

  const isOpenModal = (name: OpenModal): boolean => (
    addingModal === name
  )

  const quickAddButton = (
    name: OpenModal,
    alt: string,
  ) => (
    <QuickAddButton
      onClick={openAddingModal(name)}
      alt={alt}
    />
  )

  const quickAddProposal = () => {
    const onClick = openProposalBuilder(null)
    if (viewerCanAddProposal) {
      return (
        <QuickAddButton
          onClick={onClick}
          alt={I18n.t('side_nav.tension_processing.add_proposals')}
        />
      )
    }

    const viewerRoles = liftNodes(organization.viewer.roles)
    const canNotAddTooltip = viewerRoles.length === 0
      ? I18n.t('proposals.circle_required')
      : I18n.t('tensions.my_tensions.upgrade_to_premium')

    return (
      <DisabledButtonWithTooltip
        className={styles.disabledQuickAddProposal}
        buttonTag="button"
        id="disabled-create-proposal-sidebar"
        placement="right"
        title={<IconContainer size="sm"><Add /></IconContainer>}
        tooltip={canNotAddTooltip}
      />
    )
  }

  const quickAddButtonWithCallout = (
    name: OpenModal,
    alt: string,
  ) => {
    if (showPremiumCallouts)
      return null

    return quickAddButton(
      name,
      alt,
    )
  }

  const renderHotwireAddButton = (path: string, title: string, iconCssStyle: 'inbox' | 'generic', testId: string) => (
    <turbo-frame id="sidebar-tension-item">
      <span className="sidebar-icon-button sm">
        <a
          href={`${path}`}
          data-turbo-frame="remote-modal"
          data-test-id={testId}
          title={title}
        >
          <span className={`sidebar--add-icon-container ${iconCssStyle}`}>
            <i className="fa fa-plus" />
          </span>
        </a>
      </span>
    </turbo-frame>
  )

  const renderTensionHotwireAddButton = () => (
    renderHotwireAddButton(
      routes.addTensionPath,
      I18n.t('side_nav.tension_processing.add_tension'),
      'inbox',
      'sidebar-add-tension-button',
    )
  )

  const renderActionHotwireAddButton = () => (
    renderHotwireAddButton(
      routes.addActionPath,
      I18n.t('side_nav.tension_processing.add_action'),
      'generic',
      'sidebar-add-action-button',
    )
  )

  const usePageContentTurbo = organization.isHotwiredAlphaEnabled && document.querySelector('turbo-frame#page-content-frame')

  const items = (): SectionItemProps[] => [{
    key: '1',
    title: I18n.t('side_nav.tension_processing.inbox'),
    renderBadge: <InboxNotifications viewer={organization.viewer} />,
    renderIcon: () => (<Inbox viewer={organization.viewer} />),
    href: routes.inboxPath,
    renderActionButton: () => (
      renderTensionHotwireAddButton()
    ),
  }, organization.isApmAlphaEnabled
    ? ({
      key: '2',
      title: I18n.t('nav.workspace'),
      renderIcon: () => (<i className="fa fa-cogs" />),
      href: routes.workspacePath,
      renderActionButton: () => (
        renderActionHotwireAddButton()
      ),
    })
    : null,
  organization.isAgendaItemsEnabled
    ? ({
      key: '3',
      title: I18n.t('side_nav.tension_processing.agenda_items'),
      renderIcon: () => (<AgendaItems />),
      href: routes.agendaItemsPath,
      renderActionButton: () => (
        quickAddButton(
          'AgendaItem',
          I18n.t('side_nav.tension_processing.add_agenda_item'),
        )
      ),
    })
    : null,
  !organization.isApmAlphaEnabled
    ? ({
      key: '4',
      title: I18n.t('side_nav.tension_processing.actions'),
      renderIcon: () => (<Actions />),
      href: routes.actionsPath,
      renderActionButton: () => (
        renderActionHotwireAddButton()
      ),
    })
    : null,
  (organizationContext.show.projects && !organization.isApmAlphaEnabled)
    ? ({
      key: '5',
      title: organizationContext.labels.projects,
      renderIcon: () => (<Projects />),
      href: routes.projectsPath,
      renderActionButton: () => (
        quickAddButton(
          'Project',
          I18n.t('side_nav.tension_processing.add_project'),
        )
      ),
    })
    : null,
  organization.governanceEnabled
    ? ({
      key: '6',
      title: I18n.t('side_nav.tension_processing.proposals'),
      renderIcon: () => (<Proposals />),
      href: routes.proposalsPath,
      renderActionButton: () => quickAddProposal(),
      renderBadge: <ProposalsNotifications viewer={organization.viewer} />,
      premiumCallout: showPremiumCallouts ? 'proposals' : null,
      premiumCalloutLinkable: false,
    })
    : null,
  organizationContext.show.checklists
    ? ({
      key: '7',
      title: organizationContext.labels.checklists,
      renderIcon: () => (<Checklists />),
      href: routes.checklistsPath,
      dataTurboFrame: usePageContentTurbo ? 'page-content-frame' : null,
      renderActionButton: () => (
        quickAddButtonWithCallout(
          'Checklist',
          I18n.t('side_nav.tension_processing.add_checklist'),
        )
      ),
      premiumCallout: showPremiumCallouts ? 'checklists' : null,
      premiumCalloutLinkable: false,
    })
    : null,
  organizationContext.show.metrics
    ? ({
      key: '8',
      title: organizationContext.labels.metrics,
      renderIcon: () => (<Metrics />),
      href: routes.metricsPath,
      dataTurboFrame: usePageContentTurbo ? 'page-content-frame' : null,
      renderActionButton: () => (
        quickAddButtonWithCallout(
          'Metric',
          I18n.t('side_nav.tension_processing.add_metric'),
        )
      ),
      premiumCallout: showPremiumCallouts ? 'metrics' : null,
      premiumCalloutLinkable: false,
    })
    : null,
  organization.isGoalsEnabled
    ? ({
      key: '9',
      title: I18n.t('side_nav.tension_processing.goals'),
      renderIcon: () => (<Goals />),
      href: routes.goalsPath,
      renderActionButton: null,
    })
    : null,
  ].filter(Boolean)

  const addProject = useCallback(async (values) => {
    const response = await AddProjectMutation(values)
    return response.addProject
  }, [])

  const renderItems = () => items().map((item) => <SectionItem {...item} className={styles.item} />)

  return (
    <Fragment>
      <ul className={styles.itemsContainer} id="sidebar-tension-processing">
        {renderItems()}
      </ul>
      {isOpenModal('AgendaItem') && (
        <Suspense fallback={<ModalLoader />}>
          <LazyCreateAgendaItemModal
            close={closeAddingModal}
            organization={queryResult.organization}
          />
        </Suspense>
      )}
      {isOpenModal('Project') && (
        <Suspense fallback={<ModalLoader />}>
          <LazyCreateProjectModal
            initialValues={projectInitialValues}
            mode="CircleAndRole"
            addMutation={addProject}
            organization={queryResult.organization}
            close={closeAddingModal}
          />
        </Suspense>
      )}
      {isOpenModal('Proposal') && (
        <Suspense fallback={<ModalLoader />}>
          <LazyProposalCreator
            orgDatabaseId={organization.databaseId}
            tensionDatabaseId={tensionDatabaseId}
            toggle={async () => closeAddingModal()}
          />
        </Suspense>
      )}
      {isOpenModal('Checklist') && !showPremiumCallouts && (
        <Suspense fallback={<ModalLoader />}>
          <LazyChecklistCreator
            orgId={organization.id}
            close={closeAddingModal}
          />
        </Suspense>
      )}
      {isOpenModal('Metric') && !showPremiumCallouts && (
        <Suspense fallback={<ModalLoader />}>
          <LazyMetricCreator
            orgId={organization.id}
            close={closeAddingModal}
          />
        </Suspense>
      )}
    </Fragment>
  )
}

export default TensionProcessing
